import clsx from 'clsx';

import { BuyerIntentLeadClick } from 'components/BuyerIntent';
import ClickoutWrapper from 'components/ClickoutWrapper';
import CtaButton from 'components/CtaButton';
import Icon from 'components/Icon';
import InfoCircleTooltip from 'components/InfoCircleTooltip';
import Link from 'components/Link';
import Typography from 'components/Typography';
import { useEventContext } from 'context/EventContext';
import isPpcClickOutUrl from 'helpers/isPpcClickOutUrl';
import usePplForm from 'hooks/usePplForm';
import { handleScrollTo } from 'screens/compare/components/NavigationList/helpers';
import PricingDetails from 'screens/serp/components/SerpContentRich/components/PricingDisplay/components/PricingDetails';

import classes from './Pricing.module.scss';

const Pricing = ({ listing, showPplPricingBanner }) => {
  const hasFreeTrial = listing.is_premium && listing.cta_urls.trial;
  const hasPricingSpotlight = !!listing.cta_urls.spotlight_pricing;
  const hasVisitWebsite =
    listing.is_premium && !hasFreeTrial && !hasPricingSpotlight;
  const showPplCtas =
    showPplPricingBanner && !listing.is_premium && !hasPricingSpotlight;
  const { addEvent, sendEvent } = useEventContext();

  const {
    buyerIntentProductId,
    buyerIntentCategoryId,
    handleClick: handlePriceClick,
  } = usePplForm({
    productId: listing.global_product_id,
    sourceLocation: 'listing-detail-v1/key-takeaways:get-price-quote',
    formTemplate: 'price',
    includeProductInConfig: true,
    enabled: showPplCtas,
  });
  const { handleClick: handleDemoClick } = usePplForm({
    productId: listing.global_product_id,
    sourceLocation: 'listing-detail-v1/key-takeaways:get-demo',
    formTemplate: 'demo',
    includeProductInConfig: true,
    enabled: showPplCtas,
  });
  return (
    <div className={classes.root}>
      <div className={classes.titleContainer}>
        <InfoCircleTooltip
          className={classes.titleAndIcon}
          enterTouchDelay={100}
          leaveTouchDelay={3000}
          label={<Typography variant="title-8">Starting price</Typography>}
          title={'Pricing can vary depending on your business needs.'}
        />
        <Link
          variant="tight-xxs"
          onClick={() => {
            sendEvent({
              name: 'engagement_product_click',
              engagementComponent: 'product_overview',
              engagementDetails: 'textlink_see-pricing-details',
              engagementDestination: 'product_pricing',
            });
            handleScrollTo('pricing', 100);
          }}
        >
          See details
        </Link>
      </div>
      <div className={classes.pricingDetailContainer}>
        <PricingDetails pricing={listing.pricing} isColumn={true} />
      </div>
      <div
        className={clsx(classes.actionsContainer, {
          [classes.actionsContainerVariant]: showPplCtas,
        })}
      >
        {showPplCtas && (
          <>
            <BuyerIntentLeadClick
              productId={buyerIntentProductId}
              categoryId={buyerIntentCategoryId}
              sourceLocation="listing-detail-v1/key-takeaways:get-price-quote"
            >
              <CtaButton
                fullWidth={true}
                onClick={handlePriceClick}
                {...addEvent({
                  engagementComponent: 'ppl_banner',
                  engagementDestination: 'go-to_ppl-price-form',
                  engagementDetails: 'button_get-price-quote',
                  name: 'ppl_cta',
                })}
              >
                Get Price Quote
              </CtaButton>
            </BuyerIntentLeadClick>
            <BuyerIntentLeadClick
              productId={buyerIntentProductId}
              categoryId={buyerIntentCategoryId}
              sourceLocation="listing-detail-v1/key-takeaways:get-demo"
            >
              <CtaButton
                variant="outlined"
                fullWidth={true}
                onClick={handleDemoClick}
                {...addEvent({
                  engagementComponent: 'ppl_banner',
                  engagementDestination: 'go-to_ppl-demo-form',
                  engagementDetails: 'button_demo',
                  name: 'ppl_cta',
                })}
              >
                Demo
              </CtaButton>
            </BuyerIntentLeadClick>
          </>
        )}
        {!showPplCtas && hasVisitWebsite && (
          <ClickoutWrapper
            isClickout={isPpcClickOutUrl(listing.cta_urls.cta)}
            url={listing.cta_urls.cta}
            buyerIntentProductId={listing.global_product_id}
            buyerIntentSourceLocation="listing-detail-v1/key-takeaways:visit-website"
            buyerIntentTrackAdClick={isPpcClickOutUrl(listing.cta_urls.cta)}
            {...addEvent({
              engagementDestination: 'go-to_vendor-website',
              engagementDetails: 'button_visit-website',
              engagementComponent: 'product_overview',
              name: 'engagement_product_clickout',
              productClickUUID: listing.global_product_id,
            })}
          >
            <CtaButton fullWidth={true} component="div" className={classes.cta}>
              visit website
              <Icon
                className={classes.ctaButtonExternalLinkIcon}
                icon={['far', 'external-link']}
                size="16"
              />
            </CtaButton>
          </ClickoutWrapper>
        )}
        {!showPplCtas && hasPricingSpotlight && (
          <ClickoutWrapper
            isClickout={isPpcClickOutUrl(listing.cta_urls.spotlight_pricing)}
            url={listing.cta_urls.spotlight_pricing}
            buyerIntentProductId={listing.global_product_id}
            buyerIntentSourceLocation="listing-detail-v1/key-takeaways:view-pricing-plans"
            buyerIntentTrackAdClick={isPpcClickOutUrl(
              listing.cta_urls.spotlight_pricing
            )}
            className={classes.clickoutWrapper}
            {...addEvent({
              engagementDestination: 'go-to_vendor-website',
              engagementDetails: 'button_view-pricing-plans',
              engagementComponent: 'product_overview',
              name: 'engagement_product_clickout',
            })}
          >
            <CtaButton fullWidth={true} component="div">
              view pricing plans
              <Icon
                icon={['far', 'external-link']}
                size="16"
                className={classes.clickoutIcon}
              />
            </CtaButton>
          </ClickoutWrapper>
        )}
        {!showPplCtas && hasFreeTrial && (
          <ClickoutWrapper
            isClickout={isPpcClickOutUrl(listing.cta_urls.trial)}
            url={listing.cta_urls.trial}
            buyerIntentProductId={listing.global_product_id}
            buyerIntentSourceLocation="listing-detail-v1/key-takeaways:start-trial"
            buyerIntentTrackAdClick={isPpcClickOutUrl(listing.cta_urls.trial)}
            className={classes.clickoutWrapper}
            {...addEvent({
              engagementDestination: 'go-to_vendor-website',
              engagementDetails: 'button_start-trial',
              engagementComponent: 'product_overview',
              name: 'engagement_product_clickout',
            })}
          >
            <CtaButton
              variant={hasPricingSpotlight ? 'outlined' : 'contained'}
              fullWidth={true}
              component="div"
            >
              try for free{' '}
              <Icon
                icon={['far', 'external-link']}
                size="16"
                className={classes.clickoutIcon}
              />
            </CtaButton>
          </ClickoutWrapper>
        )}
      </div>
    </div>
  );
};
export default Pricing;
